/**
 * ARCHAT — Módulo API de mensajes y conversaciones
 * Solo UI/organización — lógica idéntica a services/api.js original
 */

import { getBasePath, API_ENDPOINTS, API_BASE, joinApiOrigin } from '../utils/constants';
import { toConversationId } from '../utils/toConversationId';
import { getToken } from '../services/apiClient';
import { devLog } from '../utils/devConsoleLog';

const basePath = (API_BASE && API_BASE.startsWith('http')) ? '' : (API_BASE || getBasePath());

export const apiMessagesMethods = {
  async getMessages(conversationId, userId, lang = 'es', subjectId = undefined, historyLimit = undefined) {
    const cid = toConversationId(conversationId);
    if (cid == null) {
      if (typeof console !== 'undefined') console.warn('[API] getMessages rejected', { reason: 'cid_null' });
      return { success: false, data: [], error: 'conversation_id_invalid' };
    }
    const safeLang = (lang && String(lang).toLowerCase().slice(0, 2)) || 'es';
    let url =
      `${API_ENDPOINTS.MESSAGES}?conversacion_id=${encodeURIComponent(cid)}` +
      `&usuario_id=${encodeURIComponent(userId)}` +
      `&lang=${encodeURIComponent(safeLang)}`;
    const sid = Number(subjectId);
    if (Number.isFinite(sid) && sid > 0) {
      url += `&subject_id=${encodeURIComponent(sid)}`;
    }
    const lim = Number(historyLimit);
    if (Number.isFinite(lim) && lim > 0) {
      url += `&limit=${encodeURIComponent(Math.min(lim, 500))}`;
    } else if (Number.isFinite(sid) && sid > 0) {
      url += '&limit=500';
    }
    const fullUrl = joinApiOrigin(url);
    try {
      if (typeof localStorage !== 'undefined' && localStorage.getItem('ARCHAT_DEBUG_SUBJECTS_CID') === '1') {
        // eslint-disable-next-line no-console
        console.info('[ARCHAT_DEBUG_SUBJECTS_CID] GET mensajes.php', fullUrl, {
          conversacion_id: cid,
          subject_id: Number.isFinite(sid) && sid > 0 ? sid : undefined,
        });
      }
    } catch {
      /* noop */
    }
    if (typeof console !== 'undefined') {
      devLog('[API][MSG] /mensajes.php (fetch GET)', fullUrl);
      devLog('[API][MSG] URL real + token', {
        fullUrl,
        basePath,
        hasToken: !!getToken(),
        VITE_API_BASE: import.meta.env?.VITE_API_BASE
      });
    }
    try {
      const result = await this.request(url, { method: 'GET', credentials: 'include' });
      devLog('[AUDIT][API_RAW_RESPONSE]', result);
      const data = Array.isArray(result?.mensajes) ? result.mensajes : (Array.isArray(result?.data) ? result.data : []);
      const success = result?.success !== false;
      if (typeof console !== 'undefined') {
        devLog('[API][MSG] response', { success, count: data.length, error: result?.error, code: result?.code, raw: result });
        devLog('[API] getMessages', { cid, success, count: data.length });
      }
      if (!success && (result?.code === 'CONVERSATION_ACCESS_DENIED' || result?.code === 'USER_MISMATCH')) {
        console.error('[HISTORY][ACCESS_DENIED]', { cid, userId, code: result?.code, error: result?.error });
      }
      const idiomaInicial = result?.idioma_inicial != null && String(result.idioma_inicial).trim() !== '' ? String(result.idioma_inicial).trim().toLowerCase().slice(0, 2) : null;
      const out = {
        success,
        data,
        mensajes: data,
        error: success ? undefined : (result?.error || 'unknown'),
        code: result?.code,
        message_key: result?.message_key,
        idioma_inicial: idiomaInicial,
        policy: result?.policy ?? null,
      };
      devLog('[AUDIT][API_RETURN]', { success: out.success, dataLength: Array.isArray(out.data) ? out.data.length : 0, hasMensajes: 'mensajes' in result });
      return out;
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.warn('[API] getMessages error', { cid, error: err?.message });
        if (err?.status === 401) console.error('[HISTORY][401] Token inválido o expirado. Cerrar sesión y volver a entrar.');
        if (err?.status === 403) console.error('[HISTORY][403] Sin acceso a esta conversación.');
      }
      return { success: false, data: [], error: err?.message || 'network_error' };
    }
  },

  async sendMessage(messageData) {
    return this.request(API_ENDPOINTS.MESSAGES, {
      method: 'POST',
      body: JSON.stringify(messageData)
    });
  },

  /**
   * Dashboard: último mensaje por conversación 1:1, más reciente primero.
   * `DashboardRecentInboundBlock` filtra en cliente a filas con `ultimo_sin_leer_para_mi` (entrante no leído).
   * @param {string|number} userId
   * @param {number} [limit]
   */
  async getDashboardRecentInbound(userId, limit = 30, uiLang) {
    const uid = encodeURIComponent(String(userId ?? '').trim());
    const lim = encodeURIComponent(String(Math.max(1, Math.min(Number(limit) || 30, 50))));
    let url = `${API_ENDPOINTS.DASHBOARD_RECENT_INBOUND}?usuario_id=${uid}&limit=${lim}`;
    const langRaw = typeof uiLang === 'string' ? uiLang.trim() : '';
    if (langRaw !== '') {
      url += `&lang=${encodeURIComponent(langRaw)}`;
    }
    try {
      const result = await this.request(url, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      });
      const ok = result?.success !== false;
      const rows = Array.isArray(result?.conversaciones)
        ? result.conversaciones
        : (Array.isArray(result?.mensajes) ? result.mensajes : []);
      return {
        success: ok,
        conversaciones: rows,
        mensajes: rows,
        error: ok ? undefined : (result?.error || 'unknown'),
      };
    } catch (err) {
      return { success: false, conversaciones: [], mensajes: [], error: err?.message || 'network_error' };
    }
  },

  /**
   * Oculta el mensaje solo para el emisor (deleted_for_sender en servidor; no borra fila).
   * @param {number|string} messageId
   * @param {number|null} conversationId
   */
  async deleteMessage(messageId, conversationId = null, deleteScope = 'everyone') {
    const mid = Number(messageId);
    if (!Number.isFinite(mid) || mid <= 0) {
      return { success: false, error: 'invalid_message_id' };
    }
    const cid = conversationId != null ? toConversationId(conversationId) : null;
    const body = JSON.stringify({
      mensaje_id: mid,
      message_id: mid,
      conversacion_id: cid,
      conversation_id: cid,
      delete_scope: deleteScope,
      scope: deleteScope,
    });
    return this.request(API_ENDPOINTS.MESSAGES, {
      method: 'DELETE',
      body,
      headers: { 'Content-Type': 'application/json' },
    });
  },

  async editMessage(messageId, conversationId = null, newText = '') {
    const mid = Number(messageId);
    if (!Number.isFinite(mid) || mid <= 0) {
      return { success: false, error: 'invalid_message_id' };
    }
    const cid = conversationId != null ? toConversationId(conversationId) : null;
    const text = String(newText ?? '').trim();
    if (!text) {
      return { success: false, error: 'empty_text' };
    }
    const body = JSON.stringify({
      mensaje_id: mid,
      message_id: mid,
      conversacion_id: cid,
      conversation_id: cid,
      nuevo_texto: text,
      text,
    });
    return this.request(API_ENDPOINTS.MESSAGES, {
      method: 'PUT',
      body,
      headers: { 'Content-Type': 'application/json' },
    });
  },

  async sendMessageWithFile(formData) {
    return this.request(API_ENDPOINTS.MESSAGES, {
      method: 'POST',
      body: formData
    });
  },

  async createConversation(conversationData) {
    devLog('=== API - CREATE CONVERSATION ===');
    devLog('conversationData:', conversationData);
    devLog('endpoint:', API_ENDPOINTS.CONVERSATIONS);
    try {
      const result = await this.request(API_ENDPOINTS.CONVERSATIONS, {
        method: 'POST',
        body: JSON.stringify(conversationData)
      });
      devLog('✅ Respuesta createConversation:', result);
      return result;
    } catch (error) {
      console.error('❌ Error en createConversation:', error);
      throw error;
    }
  },

  async getConversations(usuarioId) {
    devLog('=== API - GET CONVERSATIONS ===');
    devLog('usuarioId:', usuarioId);
    devLog('endpoint:', `${API_ENDPOINTS.CONVERSATIONS}?usuario_id=${usuarioId}`);
    try {
      const result = await this.request(`${API_ENDPOINTS.CONVERSATIONS}?usuario_id=${usuarioId}`);
      devLog('✅ Respuesta getConversations (tipo):', typeof result);
      devLog('✅ Respuesta getConversations (claves):', Object.keys(result || {}));
      devLog('✅ Respuesta getConversations (completa):', JSON.stringify(result, null, 2));
      devLog('✅ result.success:', result?.success);
      devLog('✅ result.data:', result?.data);
      devLog('✅ result.conversaciones:', result?.conversaciones);
      devLog('✅ Array.isArray(result.data):', Array.isArray(result?.data));
      devLog('✅ result.data?.length:', result?.data?.length);
      return result;
    } catch (error) {
      console.error('❌ Error en getConversations:', error);
      console.error('❌ Error stack:', error.stack);
      throw error;
    }
  },

  async createOrGetConversation(usuarioId, contactoId) {
    const payload = { usuario_id: usuarioId, contacto_id: contactoId };
    devLog('[OPEN][API] createOrGetConversation request', { endpoint: API_ENDPOINTS.CREATE_CONVERSATION, payload });
    try {
      const result = await this.request(API_ENDPOINTS.CREATE_CONVERSATION, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      devLog('[OPEN][API] createOrGetConversation response', result);
      return result;
    } catch (err) {
      if (typeof console !== 'undefined') console.error('[OPEN][API] createOrGetConversation error', err);
      throw err;
    }
  },

  async updatePresence(usuarioId, status) {
    return this.request(API_ENDPOINTS.PRESENCE, {
      method: 'PUT',
      body: JSON.stringify({ usuario_id: usuarioId, status })
    });
  },

  async getPresence(usuarioId) {
    return this.request(`${API_ENDPOINTS.PRESENCE}?usuario_id=${usuarioId}`, {
      method: 'GET',
      cache: 'no-store',
    });
  },

  async getUserStatus(userId) {
    return this.request(`${API_ENDPOINTS.USER_STATUS_GET}?usuario_id=${userId}`);
  },

  async updateUserStatus(status, customMessage = null) {
    return this.request(API_ENDPOINTS.USER_STATUS_UPDATE, {
      method: 'POST',
      body: JSON.stringify({ status, custom_message: customMessage ?? null })
    });
  },

  async markMessagesAsRead(conversacionId, usuarioId) {
    return this.request(API_ENDPOINTS.LEER, {
      method: 'POST',
      body: JSON.stringify({ conversacion_id: conversacionId, usuario_id: usuarioId })
    });
  },

  async getConversationParticipants(conversacionId, usuarioId) {
    return this.request(`${API_ENDPOINTS.CONVERSATIONS}?action=get_participants&conversacion_id=${conversacionId}&usuario_id=${usuarioId}`);
  },

  async translate(text, from, to) {
    const endpoint = API_ENDPOINTS.TRANSLATE || '/api/translate.php';
    if (typeof console !== 'undefined') {
      devLog('[TRANSLATE][API][CALL]', { endpoint, from, to, textLen: (text ?? '').length });
    }
    try {
      const res = await this.request(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          text: text ?? '',
          from: from ?? 'es',
          to: to ?? 'es'
        })
      });
      if (typeof console !== 'undefined') {
        devLog('[TRANSLATE][API][RESPONSE]', { ok: !!res, hasTranslated: !!(res?.translated ?? res?.translatedText) });
      }
      return res;
    } catch (err) {
      if (typeof console !== 'undefined') console.warn('[TRANSLATE][API]', err?.message);
      throw err;
    }
  }
};

/**
 * Invocación pura del borrado de mensaje (sin .call/.bind).
 * Desacopla el nombre del método HTTP de callbacks locales tipo `deleteMessage` en hooks (minify-safe).
 *
 * @param {{ deleteMessage: (mid: number|string, cid?: number|null) => Promise<unknown> }} apiService
 * @param {number|string} messageId
 * @param {number|null|undefined} conversationId
 */
export async function apiDeleteMessage(apiService, messageId, conversationId, deleteScope = 'everyone') {
  return apiService.deleteMessage(messageId, conversationId, deleteScope);
}

export async function apiEditMessage(apiService, messageId, conversationId, newText) {
  return apiService.editMessage(messageId, conversationId, newText);
}
